import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Wand2, 
  Clock, 
  Send, 
  Twitter, 
  Music2,
  Play,
  Eye,
  History,
  Sparkles,
  Check,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_INFO = {
  telegram: { name: 'Telegram', icon: Send, color: 'text-blue-400' },
  twitter: { name: 'Twitter', icon: Twitter, color: 'text-sky-400' },
  tiktok: { name: 'TikTok', icon: Music2, color: 'text-pink-400' },
};

const AutoPost = () => {
  const [config, setConfig] = useState({
    enabled: false,
    schedule_time: '09:00',
    timezone: 'Asia/Kolkata',
    account_ids: []
  });
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, accountsRes, historyRes] = await Promise.all([
        axios.get(`${API}/auto-post/config`),
        axios.get(`${API}/accounts`),
        axios.get(`${API}/auto-post/history?limit=5`)
      ]);
      
      if (configRes.data && configRes.data.enabled !== undefined) {
        setConfig({
          enabled: configRes.data.enabled || false,
          schedule_time: configRes.data.schedule_time || '09:00',
          timezone: configRes.data.timezone || 'Asia/Kolkata',
          account_ids: configRes.data.account_ids || []
        });
      }
      setAccounts(accountsRes.data || []);
      setHistory(historyRes.data?.history || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setAccounts([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (config.enabled && (!config.account_ids || config.account_ids.length === 0)) {
      toast.error('Please select at least one account for auto-posting');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/auto-post/config`, config);
      toast.success('Auto-post settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!config.account_ids || config.account_ids.length === 0) {
      toast.error('Please select accounts and save first');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(`${API}/auto-post/test`);
      toast.success('Test post sent successfully!');
      setHistory(prev => [response.data, ...prev].slice(0, 5));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Test failed';
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const response = await axios.get(`${API}/auto-post/preview`);
      setPreview(response.data);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const toggleAccount = (accountId) => {
    setConfig(prev => ({
      ...prev,
      account_ids: (prev.account_ids || []).includes(accountId)
        ? prev.account_ids.filter(id => id !== accountId)
        : [...(prev.account_ids || []), accountId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="auto-post-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Wand2 className="w-8 h-8 text-primary" />
          Auto-Post
        </h1>
        <p className="text-muted-foreground">
          Automatically generate and post Harry Potter themed crypto content daily.
        </p>
      </div>

      {/* Main Config */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Daily Crypto Post</CardTitle>
              <CardDescription>
                Fetches trending tokens and generates magical content automatically
              </CardDescription>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
              data-testid="auto-post-toggle"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Schedule */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Post Time
              </Label>
              <input
                type="time"
                value={config.schedule_time}
                onChange={(e) => setConfig(prev => ({ ...prev, schedule_time: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-border/50 bg-background text-foreground focus:border-primary focus:outline-none"
                data-testid="schedule-time-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={config.timezone}
                onValueChange={(value) => setConfig(prev => ({ ...prev, timezone: value }))}
              >
                <SelectTrigger data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Kolkata">IST (India)</SelectItem>
                  <SelectItem value="America/New_York">EST (New York)</SelectItem>
                  <SelectItem value="America/Los_Angeles">PST (Los Angeles)</SelectItem>
                  <SelectItem value="Europe/London">GMT (London)</SelectItem>
                  <SelectItem value="Asia/Singapore">SGT (Singapore)</SelectItem>
                  <SelectItem value="Asia/Dubai">GST (Dubai)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Account Selection */}
          <div className="space-y-3">
            <Label>Post to these accounts:</Label>
            {accounts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {accounts.map((account) => {
                  const platformInfo = PLATFORM_INFO[account.platform];
                  const Icon = platformInfo?.icon || Send;
                  const isSelected = (config.account_ids || []).includes(account.id);
                  
                  return (
                    <div
                      key={account.id}
                      onClick={() => toggleAccount(account.id)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                        isSelected 
                          ? "border-primary bg-primary/10" 
                          : "border-border/50 hover:border-border"
                      )}
                      data-testid={`account-select-${account.id}`}
                    >
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <Icon className={cn("w-4 h-4", platformInfo?.color)} />
                      <span className="font-medium">{account.account_name}</span>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {platformInfo?.name}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-sm text-yellow-200 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  No accounts connected. Please connect accounts first.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border/50">
            <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
            <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="preview-btn">
              {previewing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              Preview Post
            </Button>
            <Button 
              variant="outline" 
              onClick={handleTest} 
              disabled={testing || !config.account_ids || config.account_ids.length === 0}
              className="border-accent text-accent hover:bg-accent/10"
              data-testid="test-post-btn"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Test Post Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {preview && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg bg-muted/30 font-mono text-sm whitespace-pre-wrap">
              {preview.preview}
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Trending tokens: {preview.tokens?.map(t => t.symbol).join(', ')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Auto-Posts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <div className="space-y-3">
              {history.map((entry, index) => (
                <div 
                  key={entry.id || index}
                  className="p-4 rounded-lg bg-muted/30 border border-border/50"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <Badge variant={entry.type === 'scheduled' ? 'default' : 'outline'}>
                      {entry.type === 'scheduled' ? 'Scheduled' : 'Manual Test'}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {entry.created_at && format(new Date(entry.created_at), 'MMM d, HH:mm')}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap line-clamp-3">{entry.content}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.results?.map((result, i) => (
                      <Badge 
                        key={i}
                        className={cn(
                          "text-xs",
                          result.status === 'success' 
                            ? "bg-accent/10 text-accent" 
                            : "bg-destructive/10 text-destructive"
                        )}
                      >
                        {result.platform}: {result.status}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No auto-posts yet</p>
              <p className="text-sm">Use "Test Post Now" to try it out</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutoPost;
